import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import analytics, { appendContinuityParams } from "../../analytics";
import { toReturnQueryParams } from "../../utils/returnUrlRedirect";
import type { ReturnRedirectPayload, ReturnRedirectPostBooking } from "../../utils/returnUrlRedirect";

const REDIRECT_DELAY_MS = 3000;

interface ReturnUrlRedirectProps {
  readonly returnUrl: string;
  readonly returnMethod?: string;
  readonly payload: ReturnRedirectPayload;
  readonly postBooking: ReturnRedirectPostBooking;
  /** Resolves once the conversion beacons have been dispatched. */
  readonly purchaseTracked: React.RefObject<Promise<void>>;
}

/**
 * Adds the continuity values to a POST redirect as hidden fields.
 *
 * A POST redirect already carries its data in the form body, which is where the
 * partner reads it, so nothing is appended to the action URL: the values join
 * the body under the same names a GET redirect puts on the query string. They
 * are added immediately before submission because they are only known once GA4
 * has answered.
 */
function appendContinuityFields(form: HTMLFormElement, continuity: Record<string, string>): void {
  for (const [name, value] of Object.entries(continuity)) {
    const field = document.createElement("input");
    field.type = "hidden";
    field.name = name;
    field.value = value;
    form.appendChild(field);
  }
}

/** Sends the guest to the partner's `return_url` after a 3-second delay, via GET or POST. */
export default function ReturnUrlRedirect({
  returnUrl,
  returnMethod,
  payload,
  postBooking,
  purchaseTracked,
}: ReturnUrlRedirectProps): React.ReactElement {
  const { t } = useTranslation();
  const redirectFormRef = useRef<HTMLFormElement>(null);
  const hasRedirected = useRef(false);
  const isPost = returnMethod === "post";

  useEffect(() => {
    if (hasRedirected.current) return;

    let cancelled = false;

    const timer = globalThis.setTimeout(async () => {
      // Hold the redirect until the conversion beacons have been dispatched:
      // navigating away first tears the page down and loses them.
      await purchaseTracked.current;

      // What keeps the guest's GA4 session and decision alive across the hop.
      // Empty without a granted decision or a property of ours to read, and
      // time-boxed inside the service; a failure to read them costs the
      // continuity, never the redirect.
      const continuity = await analytics.redirectContinuityParams().catch(() => ({}));

      if (cancelled || hasRedirected.current) return;
      hasRedirected.current = true;

      if (isPost) {
        const form = redirectFormRef.current;
        if (!form) return;

        appendContinuityFields(form, continuity);
        form.submit();
      } else {
        // Falls back to the raw URL when it is not one we can parse.
        globalThis.location.href = appendContinuityParams(returnUrl, {
          ...toReturnQueryParams(payload),
          ...continuity,
        });
      }
    }, REDIRECT_DELAY_MS);

    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [returnUrl, isPost, payload, purchaseTracked]);

  return (
    <div className="gcw:text-center gcw:text-md gcw:text-text-muted gcw:py-bs-3" aria-live="polite">
      <p>{t("booking_redirecting")}</p>
      <output className="loading-spinner gcw:mt-2 gcw:block" aria-label={t("loading")} />

      {isPost && (
        <form ref={redirectFormRef} method="POST" action={returnUrl} className="gcw:hidden" aria-hidden="true">
          <input type="hidden" name="booking" value={JSON.stringify(postBooking)} />
        </form>
      )}
    </div>
  );
}
